import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLogoutMutation, useUserInfoQuery } from '~/apis/auth';
import { useIpBrandListQuery } from '~/apis/portfolio/ipDocuments';
import { userProfileAtom } from '~/atoms';
import { useLogger, useSelectedBrandId } from '~/hooks';
import { RootRouteEnum } from '~/router/@types';
import {
  MarqCommonEnumsClientClientRoleEnum,
  PortfolioIpDocProtocolsGetBrandListResponse,
} from '~/swagger/data-contracts';
import { ALL_BRANDS_LABEL } from '../@constants';

export const useViewModel = (params: { brandUnselectable?: boolean }) => {
  const { data: auth } = useAuth(); // 여기에 있는 role은 새로고침해도 변하지 않는다
  const { data: freshUserInfo } = useUserInfoQuery();
  const currentUserRole = freshUserInfo?.role;
  const brandListQuery = useIpBrandListQuery({ enabled: !!freshUserInfo, select: parseBrandList });
  const { mutateAsync, isLoading } = useLogoutMutation();
  const { brandId = null, onBrandChange } = useSelectedBrandId();

  // PROD-9645 에 의해 brandUnselectable 조건에서 "&& !!selectedBrandId;" 조건 제외
  const brandUnselectable = !!params.brandUnselectable; // && !!selectedBrandId;

  const brandOption = [
    { label: freshUserInfo?.client_name || ALL_BRANDS_LABEL, value: null },
    ...(brandListQuery.data?.map((brand) => ({ label: brand.name, value: brand.id })) || []),
  ];
  const selectedBrandName =
    brandListQuery.data?.find((brand) => brand.id === brandId)?.name || freshUserInfo?.client_name || ALL_BRANDS_LABEL;

  const userProfile = useAtomValue(userProfileAtom);
  const userCountries = userProfile?.limited_access_policy?.countries?.map((country) => ({
    label: country.name,
    value: country.code,
  }));
  const isUserAvailableAllBrand =
    currentUserRole === MarqCommonEnumsClientClientRoleEnum.LimitedAccess
      ? userProfile?.limited_access_policy?.has_all_brand_access
      : true;
  const isUserAvailableAllCountry =
    currentUserRole === MarqCommonEnumsClientClientRoleEnum.LimitedAccess
      ? userProfile?.limited_access_policy?.has_all_country_access
      : true;

  const handleChangeBrand = (value: number) => {
    onBrandChange(value);
  };

  const { amplitudeLogging } = useLogger();
  const navigate = useNavigate();
  const handleLogout = async () => {
    amplitudeLogging('sign out');
    await mutateAsync({
      access_token: auth?.access_token,
    });
    navigate(`/${RootRouteEnum.Login}`);
  };

  useEffect(() => {
    if (!brandListQuery.data) return;
    if (brandId && !brandListQuery.data.some((v) => v.id === brandId)) {
      onBrandChange(undefined);
    }
    if (brandListQuery.data.length === 1) {
      onBrandChange(brandListQuery.data[0].id);
    }
  }, [onBrandChange, brandListQuery.data, brandId]);

  return {
    client: {
      name: freshUserInfo?.client_name,
      logoUrl: freshUserInfo?.logo_url,
    },
    brand: {
      isSuccess: brandListQuery.isSuccess,
      selectedName: selectedBrandName,
      selectedId: brandId,
      onChange: handleChangeBrand,
      list: brandListQuery.data || [],
      options: brandOption,
      unselectable: brandUnselectable,
      isAvailableAll: isUserAvailableAllBrand,
    },
    country: {
      options: userCountries,
      isAvailableAll: isUserAvailableAllCountry,
    },
    user: {
      firstName: userProfile?.first_name,
      onLogout: handleLogout,
      role: freshUserInfo?.role,
    },
    isLoading,
  };
};

const parseBrandList = (apiData: PortfolioIpDocProtocolsGetBrandListResponse) => {
  return apiData.brand_list.map((brand) => ({
    id: brand.brand_id,
    copyrights: brand.copyrights,

    designs: brand.designs,
    logoUrl: brand.logo_url,
    name: brand.name,

    trademarks: brand.trademarks,

    updatedDate: brand.updated_date,
  }));
};
